<template>
  <div class="entered-contests">
    <template v-for="contest in contestEntries">
      <div class="contest" :key="contest.contestId" @click="onClickContest(contest.contestId)">
        <div class="contest-data">
          <div class="left">
            <img :src="contestImage(contest)" alt="contest image" class="contest-image" loading="lazy" />
            <div class="contest-name">{{ contest.contestName }}</div>
          </div>
          <div class="right">
            <div class="entry-count">{{ $t("enteredContests.entryCount", { count: contest.entryNovels.length }) }}</div>
            <ChevronDownIcon class="caret" :class="{ open: isOpen(contest.contestId) }" />
          </div>
        </div>
        <div v-if="isOpen(contest.contestId)" class="entry-novels" :key="`${contest.contestId}_novels`">
          <div class="label-title">{{ $t("enteredContests.labelTitle") }}</div>
          <template v-for="novel in contest.entryNovels">
            <div
              class="novel"
              :key="novel.entryId"
              :class="{ 'no-link': !novel.novelId }"
              @click.stop="novel.novelId && onClickNovel(novel.novelId)"
            >
              <div class="novel-data">
                <ImageView class="image" :src="novelImage(novel.novelId) || placeholderImage" />
                <div class="data">
                  <div v-if="novel.categoryName" class="category">{{ novel.categoryName }}</div>
                  <div class="title">{{ novelTitle(novel.novelId) }}</div>
                  <div class="information">
                    <div class="status">{{ novel.entryStatusText }}</div>
                    <div class="date">{{ $t("enteredContests.entryDate", { date: formatDate(novel.entryTime) }) }}</div>
                  </div>
                </div>
              </div>
              <div class="caution">{{ $t("enteredContests.caution") }}</div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <div v-if="contestEntries.length === 0" class="no-entry">{{ $t("enteredContests.noEntry") }}</div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import axiosBase from "axios";
import ChevronDownIcon from "icons/ChevronDown.vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { ContestEntries } from "@/lib/models/nolaContest";
import {
  NolaPfContestEntries,
  NolaPfContestEntryStatus,
  NolaPfMicroCmsContestImage,
} from "@/lib/models/nolaPfContest";
import { NolaPfContestClient } from "@/lib/clients";
import { Novel } from "@/lib/models";
import { format } from "date-fns";
import { optimizeImage } from "@/lib/utils/url";
import { retryableGet } from "@/lib/utils/networkRetry";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

const nolaPfContestClient = new NolaPfContestClient();

const placeholderImage = "/img/placeholders/novel.png";

/** 一覧表示用に 2 つの API のレスポンスを統一した形。 */
interface DisplayEntryNovel {
  entryId: string;
  novelId: string | null;
  categoryName?: string;
  entryStatusText: string;
  entryTime: number | string;
}

interface DisplayContestEntries {
  contestId: string;
  contestName: string;
  /** NolaPfContest 側から取得できたアイキャッチ。無ければ microCMS から別途取得する。 */
  image?: NolaPfMicroCmsContestImage | null;
  entryNovels: DisplayEntryNovel[];
}

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { ChevronDownIcon, ImageView },
  data() {
    return {
      contests: [],
      pfContestEntries: [],
      openedContests: [],
      placeholderImage,
    };
  },
  async created() {
    // NolaPfContest の応募履歴一覧 API と既存の応募履歴一覧 API を並列に呼び出す
    const legacyPromise = this.$store.dispatch("nolaContestModule/fetchContestEntries");
    const pfEntriesPromise = nolaPfContestClient.fetchEntries().catch((error) => {
      console.error("Failed to fetch NolaPfContest entries:", error);
      return [];
    });
    this.$store.dispatch("novelModule/initialize");

    const [, pfContestEntries] = await Promise.all([legacyPromise, pfEntriesPromise]);
    this.pfContestEntries = pfContestEntries;
  },
  computed: {
    legacyContestEntries() {
      return this.$store.getters["nolaContestModule/contestEntries"];
    },
    pfContestEntriesDisplay() {
      return this.pfContestEntries.map((contest) => ({
        contestId: contest.contestId,
        contestName: contest.name ?? "",
        image: contest.image,
        entryNovels: contest.entries.map((entry) => ({
          entryId: entry.entryId,
          novelId: entry.novelId,
          entryStatusText: this.pfStatusText(entry.status),
          entryTime: entry.createdAt,
        })),
      }));
    },
    contestEntries() {
      // NolaPfContest の応募履歴を先頭に連結して表示する
      return [...this.pfContestEntriesDisplay, ...this.legacyContestEntries];
    },
    pfStatusText() {
      return (status: NolaPfContestEntryStatus) => this.$t(`enteredContests.pfStatus.${status}`) as string;
    },
    contestImage() {
      return (contest) => {
        if (contest.image) {
          return optimizeImage(`${contest.image.url}?fit=crop&w=200&h=100`);
        }
        const legacyContest = this.contests.find((c: any) => c.contestId === contest.contestId);
        return legacyContest && optimizeImage(`${legacyContest.image.url}?fit=crop&w=200&h=100`);
      };
    },
    isOpen() {
      return (contestId) => this.openedContests.includes(contestId);
    },
    novel() {
      return (novelId) => this.$store.getters["novelModule/novel"](novelId);
    },
    novelImage() {
      return (novelId) => {
        const novel = this.novel(novelId);
        if (!novel) return undefined;

        const { image } = novel;
        return image && image.startsWith("file:") ? `file:novels/${novelId}/cover.jpg` : image;
      };
    },
    novelTitle() {
      return (novelId) => {
        const novel = this.novel(novelId);
        return novel && novel.title;
      };
    },
    formatDate() {
      return (date) => format(date, "YYYY/MM/DD");
    },
  },
  methods: {
    onClickContest(contestId) {
      if (this.openedContests.includes(contestId)) {
        this.openedContests = this.openedContests.filter((id) => id !== contestId);
      } else {
        this.openedContests.push(contestId);
      }
    },
    onClickNovel(novelId) {
      this.$router.push({ name: "editor", params: { novelId } }).catch((error) => {
        console.error(error);
      });
    },
  },
  watch: {
    // 既存の応募履歴一覧 API 由来のコンテストについてのみ、microCMS からアイキャッチ等を取得する
    // （NolaPfContest 由来のコンテストは API レスポンスに image を含むため対象外）
    legacyContestEntries: {
      async handler(value: ContestEntries[]) {
        if (value.length > 0) {
          const filterParams = value.reduce(
            (acc, cur) => `${acc}${acc && "[or]"}contestId[equals]${cur.contestId}`,
            ""
          );
          const result = await retryableGet(axios, `/contest?filters=${filterParams}&limit=100`);
          this.contests = result.data.contents;
        }
      },
      deep: true,
    },
  },
});

interface Props {}

interface Data {
  contests: { [key: string]: any }[];
  pfContestEntries: NolaPfContestEntries[];
  openedContests: string[];
  placeholderImage: string;
}

interface Computed {
  legacyContestEntries: ContestEntries[];
  pfContestEntriesDisplay: DisplayContestEntries[];
  contestEntries: DisplayContestEntries[];
  pfStatusText: (status: NolaPfContestEntryStatus) => string;
  contestImage: (contest: DisplayContestEntries) => string | undefined;
  isOpen: (contestId: string) => boolean;
  novel: (novelId: string | null) => Novel | undefined;
  novelImage: (novelId: string | null) => string | undefined;
  novelTitle: (novelId: string | null) => string | undefined;
  formatDate: (date: number | string) => string;
}

interface Methods {
  onClickContest: (contestId: string) => void;
  onClickNovel: (novelId: string) => void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.entered-contests {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 100px;
  overflow: auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.contest {
  display: flex;
  flex-direction: column;

  margin-bottom: 20px;
  box-sizing: border-box;
  padding: 20px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  background-color: var(--color-surface-base);

  &:last-of-type {
    margin-bottom: 0;
  }

  .contest-data {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left,
    .right {
      display: flex;
      align-items: center;
    }

    .contest-image {
      height: 100px;
      margin-right: 20px;
    }

    .contest-name {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-md);
      color: var(--color-text-normal);
    }

    .entry-count {
      margin-right: 20px;
      box-sizing: border-box;
      padding: 5px 10px;
      border: 1px solid var(--color-inactive);
      border-radius: var(--radius-rounded);
      background-color: var(--color-inactive);
      color: var(--color-text-on-fixed-bg);
    }

    .caret {
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  .caution {
    margin-top: 20px;
    font-size: var(--font-size-xs);
    color: var(--color-text-low);
  }

  .entry-novels {
    box-sizing: border-box;
    padding-top: 20px;

    .label-title {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-md);
      color: var(--color-text-on-fixed-bg);
      background-color: var(--color-inactive);
      box-sizing: border-box;
      padding: 8px 16px;
      margin: 16px 0;
    }

    .novel {
      position: relative;
      @include hover-overlay;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      padding: 20px 0;
      border-bottom: 2px solid var(--color-border);
      cursor: pointer;

      &.no-link {
        cursor: default;
      }

      &:first-of-type {
        padding-top: 0;
      }

      &:last-of-type {
        padding-bottom: 0;
        border-bottom: none;
      }

      .novel-data {
        display: flex;
        align-items: center;

        .image {
          height: 100px;
          width: auto;
          margin-right: 20px;
        }

        .data {
          flex: 1;

          display: flex;
          flex-direction: column;
          justify-content: space-between;

          .category {
            color: var(--color-text-low);
          }

          .title {
            font-weight: var(--font-weight-bold);
            font-size: 1.2em;
            margin: 1em 0;
            color: var(--color-text-normal);
          }

          .information {
            display: flex;
            justify-content: space-between;

            .status {
              color: var(--color-primary);
            }

            .date {
              color: var(--color-text-low);
            }
          }
        }
      }
    }
  }
}

.no-entry {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: var(--font-size-lg);
  color: var(--color-text-low);
}
</style>
